import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Eye, Printer } from 'lucide-react';

const StatusBadge = ({ status }) => {
  const styles = {
    complete: 'bg-blue-50 text-blue-700 border-blue-200', in_progress: 'bg-amber-50 text-amber-700 border-amber-200', abandoned: 'bg-zinc-100 text-zinc-400 border-zinc-200',
  };
  return <Badge variant="outline" className={`rounded-sm text-[10px] font-semibold ${styles[status] || ''}`}>{(status || '').replace('_', ' ')}</Badge>;
};

export default function AllRunsPage() {
  const [runs, setRuns] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = statusFilter ? `?status=${statusFilter}` : '';
    api.get(`/runs${params}`).then(r => { setRuns(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [statusFilter]);

  return (
    <div className="p-6" data-testid="all-runs-page">
      <p className="text-sm text-zinc-500 mb-4">View all checklist runs across staff</p>

      <div className="flex gap-2 mb-4">
        {['', 'in_progress', 'complete', 'abandoned'].map(s => (
          <Button key={s} variant={statusFilter === s ? 'default' : 'outline'} size="sm" onClick={() => setStatusFilter(s)}
            className={`rounded-sm text-xs ${statusFilter === s ? 'bg-zinc-900 text-white' : ''}`}>
            {s ? s.replace('_', ' ') : 'All'}
          </Button>
        ))}
      </div>

      {loading ? <div className="text-sm text-zinc-400 py-8">Loading...</div> : runs.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-sm px-4 py-12 text-center text-sm text-zinc-400">No runs found</div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-sm">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Project</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Client</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Engineer</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Status</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Created</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 px-4">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map(run => (
                <TableRow key={run.id}>
                  <TableCell className="px-4 py-2.5 font-medium text-zinc-800">{run.project_fields?.project_name || '-'}</TableCell>
                  <TableCell className="px-4 py-2.5 text-zinc-500">{run.project_fields?.client_name || '-'}</TableCell>
                  <TableCell className="px-4 py-2.5 text-zinc-500">{run.project_fields?.engineer_name || '-'}</TableCell>
                  <TableCell className="px-4 py-2.5"><StatusBadge status={run.status} /></TableCell>
                  <TableCell className="px-4 py-2.5 text-zinc-500 text-xs">{run.created_at ? new Date(run.created_at).toLocaleDateString() : '-'}</TableCell>
                  <TableCell className="px-4 py-2.5">
                    <div className="flex gap-1">
                      <Link to={`/checklists/${run.id}/complete`} className="inline-flex items-center justify-center h-7 w-7 rounded-sm text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"><Eye size={14} /></Link>
                      {run.status === 'complete' && <Link to={`/checklists/${run.id}/print`} className="inline-flex items-center justify-center h-7 w-7 rounded-sm text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"><Printer size={14} /></Link>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
